/**
 * draftRenderer.js
 *
 * Single shared HTML renderer for ALL draft documents — used by both
 * DocumentLibraryPage (via documentDraftFormatter) and ResolutionsPage.
 *
 * Accepts a structured `spec` + `companyContext` and produces a fully
 * self-contained inline-styled HTML string (no external CSS needed).
 *
 * Public API:
 *   renderDraftHtml(spec, companyContext) → string
 */

import { marked } from 'marked';
import { escapeHtml, formatDocumentDate } from './draftUtils';
import { buildCertificationLine } from './certification';
import { sanitizeDocHtml } from './sanitizeHtml';

marked.setOptions({ gfm: true, breaks: false });

// ── Inline style constants ────────────────────────────────────────────────────
const PARA   = 'font-size:12pt;line-height:1.85;margin:0 0 10px 0;text-align:justify';
const RESOLV = 'font-size:12pt;line-height:1.8;margin:10px 0 10px 24px;padding-left:12px;border-left:3px solid #333;font-style:italic;text-align:justify';
const RICH   = 'font-size:12pt;line-height:1.6;margin:10px 0';

// Detect GFM-flavoured structure (table separator row, list bullets, headings).
// Lines that look like Markdown are rendered via marked so tables / lists survive
// the AI regeneration round-trip instead of getting flattened into plain text.
const MARKDOWN_STRUCTURE_RE = /(\n\s*\|[\s\-:|]+\|)|(^\s*[*\-]\s)|(^\s*\d+\.\s)|(^\s*#)/m;
const looksLikeMarkdown = (line = '') => MARKDOWN_STRUCTURE_RE.test(String(line));

/**
 * Render a single line as either a RESOLVED block, a markdown structure
 * (table / list / heading), or a standard paragraph. Used when the caller has
 * already merged body + resolved into one flat list (document-library path).
 */
function renderLine(line) {
  if (/^(resolved that|further resolved that)/i.test(line)) {
    return `<div style="${RESOLV}">${escapeHtml(line)}</div>`;
  }
  if (looksLikeMarkdown(line)) {
    return `<div style="${RICH}">${sanitizeDocHtml(marked.parse(String(line)))}</div>`;
  }
  return `<p style="${PARA}">${escapeHtml(line)}</p>`;
}

/**
 * @param {object} spec
 *   title               string  — document / resolution title
 *   section             string  — reference / section string (optional)
 *   certifiedLabel      string  — override for the certified-true-copy box (optional)
 *   isResolution        bool    — true = resolution layout (separate resolved blocks)
 *   bodyParagraphs      string[] — narrative paragraphs
 *   resolvedClauses     string[] — RESOLVED THAT … lines
 *   furtherResolvedClauses string[] — FURTHER RESOLVED THAT … lines
 *   signatories         {name, designation, din?}[]
 *
 * @param {object} companyContext   normalizeCompanyContext() output
 */
export function renderDraftHtml(spec = {}, companyContext = {}) {
  const {
    title             = '[DOCUMENT TITLE]',
    section           = '',
    certifiedLabel,
    isResolution      = false,
    bodyParagraphs    = [],
    resolvedClauses   = [],
    furtherResolvedClauses = [],
    signatories       = [],
  } = spec;

  // ── Company fields with fallbacks ─────────────────────────────────────────
  const co = {
    name    : companyContext.name     || '[COMPANY NAME]',
    cin     : companyContext.cin      || '[CIN / LLPIN]',
    address : companyContext.address  || '[REGISTERED ADDRESS]',
    type    : companyContext.type     || '[COMPANY TYPE]',
    state   : companyContext.state    || '[STATE]',
    roc     : companyContext.roc      || '[ROC]',
    auditor : companyContext.auditor  || '',
    authCap : companyContext.authorisedCapital || '',
    paidUp  : companyContext.paidUpCapital     || '',
    // R5 — the date picked in the draft modal (ISO or display text). Absent, the
    // meeting-details table falls back to today, as it always did.
    date    : formatDocumentDate(companyContext.resolutionDate || companyContext.documentDate),
  };

  // ── Certified-copy box text ───────────────────────────────────────────────
  const certText = certifiedLabel || (
    isResolution
      ? buildCertificationLine({ companyName: co.name, address: co.address })
      : `CERTIFIED TRUE COPY OF THE DRAFT ${title.toUpperCase()} PREPARED FOR ${co.name} FOR INTERNAL REVIEW AND FILING PURPOSES.`
  );

  // ── Body HTML ─────────────────────────────────────────────────────────────
  const narrativeHtml = bodyParagraphs.map(renderLine).join('');

  const resolvedHtml = [
    ...resolvedClauses.map(c => `<div style="${RESOLV}">${escapeHtml(c)}</div>`),
    ...furtherResolvedClauses.map(c => `<div style="${RESOLV}">${escapeHtml(c)}</div>`),
  ].join('');

  // ── Signature block ───────────────────────────────────────────────────────
  // Adaptive layout: max 2 per row on A4; single col when there's only 1 sig
  // so the cell doesn't sit beside an empty box; page-break-inside:avoid keeps
  // the whole block on one printed page; word-wrap handles long names.
  const sigList = signatories.length > 0
    ? signatories
    : [
        { name: '[Authorised Signatory]', designation: '[Designation]' },
        { name: '[Name]', designation: '[Witness / Director]' },
      ];
  const sigCols = sigList.length === 1 ? 1 : 2;

  const sigHtml = sigList
    .map(
      (s) => `
      <div style="min-width:0">
        <div style="border-top:1.5px solid #000;padding-top:6px;margin-top:40px;word-wrap:break-word">
          <div style="font-weight:bold;font-size:12pt">${escapeHtml(s.name || '[Name]')}</div>
          <div style="font-size:11pt">${escapeHtml(s.designation || '[Designation]')}</div>
          ${s.din ? `<div style="font-size:10.5pt;color:#444">DIN: ${escapeHtml(s.din)}</div>` : ''}
        </div>
      </div>`,
    )
    .join('');

  // ── Optional capital line ─────────────────────────────────────────────────
  const capitalLine =
    co.authCap || co.paidUp
      ? `<div style="font-size:11pt;margin-top:4px">` +
        (co.authCap ? `<strong>Auth. Capital:</strong> ₹${escapeHtml(co.authCap)}` : '') +
        (co.authCap && co.paidUp ? ' &nbsp;|&nbsp; ' : '') +
        (co.paidUp ? `<strong>Paid-Up:</strong> ₹${escapeHtml(co.paidUp)}` : '') +
        `</div>`
      : '';

  // ── Auditor line (only when present) ─────────────────────────────────────
  const auditorSnippet =
    co.auditor ? ` | <strong>Auditor:</strong> ${escapeHtml(co.auditor)}` : '';

  // ── Final HTML ────────────────────────────────────────────────────────────
  return `
<div style="text-align:center;border-bottom:3px double #000;padding-bottom:18px;margin-bottom:22px">
  <div style="font-size:16pt;font-weight:bold;text-transform:uppercase;letter-spacing:2px;margin-bottom:5px">${escapeHtml(co.name)}</div>
  <div style="font-size:10pt;color:#333;margin-bottom:4px">${escapeHtml(co.type)} | ${escapeHtml(co.state)} | ROC: ${escapeHtml(co.roc)}</div>
  <div style="font-size:10pt;font-style:italic;color:#555;margin-bottom:4px"><strong>CIN / LLPIN:</strong> ${escapeHtml(co.cin)}${auditorSnippet}</div>
  <div style="font-size:11pt;margin-top:4px">${escapeHtml(co.address)}</div>
  ${capitalLine}
</div>

<hr style="border:none;border-top:3px double #000;margin:14px 0" />

<div style="font-size:11.5pt;font-weight:bold;text-align:center;text-transform:uppercase;border:1px solid #000;padding:10px 14px;margin:14px 0;line-height:1.7">
  ${escapeHtml(certText)}
</div>

<table style="width:100%;border-collapse:collapse;margin-bottom:16px">
  <tr>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top;font-weight:bold;width:180px;white-space:nowrap">Company</td>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top">: ${escapeHtml(co.name)}</td>
  </tr>
  <tr>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top;font-weight:bold;width:180px;white-space:nowrap">CIN / LLPIN</td>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top">: ${escapeHtml(co.cin)}</td>
  </tr>
  <tr>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top;font-weight:bold;width:180px;white-space:nowrap">Date</td>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top">: ${escapeHtml(co.date)}</td>
  </tr>
  <tr>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top;font-weight:bold;width:180px;white-space:nowrap">Venue</td>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top">: Registered Office of the Company, ${escapeHtml(co.address)}</td>
  </tr>
  <tr>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top;font-weight:bold;width:180px;white-space:nowrap">Subject</td>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top">: ${escapeHtml(title)}</td>
  </tr>
  ${section ? `
  <tr>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top;font-weight:bold;width:180px;white-space:nowrap">Reference</td>
    <td style="padding:6px 8px;font-size:12pt;vertical-align:top">: ${escapeHtml(section)}</td>
  </tr>` : ''}
</table>

<hr style="border:none;border-top:1.5px solid #000;margin:14px 0" />
${narrativeHtml}
${resolvedHtml}
<hr style="border:none;border-top:1.5px solid #000;margin:14px 0" />

<p style="font-size:12pt;margin:0 0 10px 0"><strong>Certified to be true draft copy.</strong></p>
<p style="font-size:12pt;margin:0 0 10px 0"><strong>For ${escapeHtml(co.name)}</strong></p>

<div style="display:grid;grid-template-columns:repeat(${sigCols},1fr);gap:40px 60px;margin-top:44px;page-break-inside:avoid">
  ${sigHtml}
</div>

<div style="text-align:center;font-size:9pt;color:#777;border-top:1px solid #ccc;padding-top:8px;margin-top:40px">
  ${escapeHtml(co.name)} | ${escapeHtml(co.cin)}
</div>
`.trim();
}
